import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useGlobalStorageContext } from '../contexts/GlobalStorageContext';
import { formatHijriDate } from '../utils/hijriDate';

export const HijriPageSimple: React.FC = () => {
  const { colors } = useTheme();
  return (
    <div style={{ padding: '20px', backgroundColor: colors.background, color: colors.text, minHeight: '100vh' }}>
      <h2>Hijri Calendar</h2>
      <div style={{ backgroundColor: colors.cardBackground, padding: '24px', borderRadius: '16px', marginTop: '20px', border: `1px solid ${colors.border}` }}>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: colors.tint, marginBottom: '12px' }}>Today</div>
        <div style={{ fontSize: '18px' }}>{formatHijriDate(new Date())}</div>
      </div>
    </div>
  );
};

export const EventsPage: React.FC = () => {
  const { colors } = useTheme();
  const events = [
    { year: 2025, ramadanStart: 'March 1', eidAlAdha: 'June 6' },
    { year: 2026, ramadanStart: 'February 18', eidAlAdha: 'May 27' },
    { year: 2027, ramadanStart: 'February 8', eidAlAdha: 'May 17' },
    { year: 2028, ramadanStart: 'January 28', eidAlAdha: 'May 5' },
    { year: 2029, ramadanStart: 'January 16', eidAlAdha: 'April 24' },
    { year: 2030, ramadanStart: 'January 6', eidAlAdha: 'April 14' },
    { year: 2031, ramadanStart: 'December 26', eidAlAdha: 'April 3' },
    { year: 2032, ramadanStart: 'December 15', eidAlAdha: 'March 23' },
    { year: 2033, ramadanStart: 'December 4', eidAlAdha: 'March 12' },
    { year: 2034, ramadanStart: 'November 23', eidAlAdha: 'March 2' },
    { year: 2035, ramadanStart: 'November 12', eidAlAdha: 'February 20' },
  ];

  const currentYear = new Date().getFullYear();
  const currentYearEvent = events.find(e => e.year === currentYear);

  return (
    <div style={{ padding: '20px', backgroundColor: colors.background, minHeight: '100vh', color: colors.text }}>
      <h2 style={{ color: colors.tint }}>Islamic Events</h2>
      
      {currentYearEvent && (
        <>
          <h3 style={{ marginTop: '24px', marginBottom: '16px' }}>This Year ({currentYear})</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <div style={{ backgroundColor: colors.cardBackground, borderRadius: '16px', padding: '20px', border: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: '10px', fontWeight: 'bold', color: colors.tint, opacity: 0.6, marginBottom: '8px', letterSpacing: '1px' }}>RAMADAN</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{currentYearEvent.ramadanStart}</div>
            </div>
            <div style={{ backgroundColor: colors.cardBackground, borderRadius: '16px', padding: '20px', border: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: '10px', fontWeight: 'bold', color: colors.tint, opacity: 0.6, marginBottom: '8px', letterSpacing: '1px' }}>EID AL-ADHA</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{currentYearEvent.eidAlAdha}</div>
            </div>
          </div>
        </>
      )}

      <h3 style={{ marginBottom: '16px' }}>10-Year Overview</h3>
      <div style={{ backgroundColor: colors.cardBackground, borderRadius: '16px', padding: '20px', border: `1px solid ${colors.border}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Year</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Ramadan</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Eid al-Adha</th>
            </tr>
          </thead>
          <tbody>
            {events.map(ev => (
              <tr key={ev.year} style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '12px', fontWeight: 'bold' }}>{ev.year}</td>
                <td style={{ padding: '12px', color: colors.muted }}>{ev.ramadanStart}</td>
                <td style={{ padding: '12px', color: colors.muted }}>{ev.eidAlAdha}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const KahfPage: React.FC = () => {
  const { colors } = useTheme();
  const [surahKahf, setSurahKahf] = React.useState<any>(null);
  const [loadingSurah, setLoadingSurah] = React.useState(false);
  const [activeVerseNum, setActiveVerseNum] = React.useState<number | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  React.useEffect(() => {
    setLoadingSurah(true);
    fetchSurah(18).then(data => {
      setSurahKahf(data);
      setLoadingSurah(false);
    });

    return () => {
      stopAllAudio();
    };
  }, []);

  const fetchSurah = async (surahNumber: number) => {
    try {
      const arabicResponse = await fetch(`https://api.alquran.cloud/v1/surah/${surahNumber}`);
      const arabicData = await arabicResponse.json();
      
      const translationResponse = await fetch(`https://api.alquran.cloud/v1/surah/${surahNumber}/en.asad`);
      const translationData = await translationResponse.json();
      
      if (arabicData.code === 200 && translationData.code === 200) {
        const surah = arabicData.data;
        const translation = translationData.data;
        
        const verses = surah.ayahs.map((ayah: any, index: number) => ({
          number: ayah.number,
          numberInSurah: ayah.numberInSurah,
          text: ayah.text,
          translation: translation.ayahs[index]?.text || ''
        }));
        
        return {
          number: surah.number,
          name: surah.name,
          englishName: surah.englishName,
          englishNameTranslation: surah.englishNameTranslation,
          numberOfAyahs: surah.numberOfAyahs,
          revelationType: surah.revelationType,
          verses
        };
      }
    } catch (error) {
      console.error('Error fetching Surah:', error);
    }
  };

  const stopAllAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setActiveVerseNum(null);
    setIsAutoPlaying(false);
  };

  const playVerse = (verse: any, autoNext = false) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const surahNum = "018";
    const verseStr = verse.numberInSurah.toString().padStart(3, '0');
    const url = `https://everyayah.com/data/Alafasy_128kbps/${surahNum}${verseStr}.mp3`;
    
    const audio = new Audio(url);
    audioRef.current = audio;
    setActiveVerseNum(verse.numberInSurah);
    audio.play();
    
    audio.onended = () => {
      if (autoNext && surahKahf) {
        const nextIdx = surahKahf.verses.findIndex((v: any) => v.numberInSurah === verse.numberInSurah) + 1;
        if (nextIdx < surahKahf.verses.length) {
          playVerse(surahKahf.verses[nextIdx], true);
        } else {
          stopAllAudio();
        }
      } else {
        setActiveVerseNum(null);
      }
    };
  };

  const toggleAutoPlay = () => {
    if (isAutoPlaying) {
      stopAllAudio();
    } else if (surahKahf) {
      setIsAutoPlaying(true);
      playVerse(surahKahf.verses[0], true);
    }
  };

  return (
    <div style={{ backgroundColor: colors.background, minHeight: '100vh', color: colors.text }}>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h2 style={{ color: colors.tint, marginBottom: '8px' }}>
              {surahKahf ? surahKahf.englishName : 'Surah Al-Kahf'}
            </h2>
            <p style={{ fontSize: '12px', color: colors.muted }}>
              {surahKahf ? `${surahKahf.numberOfAyahs} Ayahs • ${surahKahf.revelationType}` : "Loading..."}
            </p>
          </div>
          <button
            onClick={toggleAutoPlay}
            disabled={loadingSurah || !surahKahf}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', borderRadius: '12px', backgroundColor: isAutoPlaying ? colors.accent : colors.border, border: 'none', cursor: loadingSurah ? 'not-allowed' : 'pointer', fontSize: '10px', fontWeight: 'bold', color: colors.text, textTransform: 'uppercase' }}
          >
            <span>{isAutoPlaying ? '⏸' : '▶'}</span>
            <span>{isAutoPlaying ? 'PAUSE' : 'LISTEN FULL'}</span>
          </button>
        </div>
        
        <div style={{ backgroundColor: colors.cardBackground, borderRadius: '24px', padding: '24px', border: `1px solid ${colors.border}` }}>
          {loadingSurah ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
              <div style={{ fontSize: '14px', color: colors.tint, fontWeight: 'bold' }}>Fetching Ayahs...</div>
            </div>
          ) : surahKahf?.verses.map((v: any) => (
            <div
              key={v.number}
              style={{ marginBottom: '32px', padding: '16px', borderRadius: '16px', backgroundColor: activeVerseNum === v.numberInSurah ? colors.background : 'transparent' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 'bold', color: colors.tint, textTransform: 'uppercase', letterSpacing: '2px' }}>AYAH {v.numberInSurah}</div>
                <button
                  onClick={() => activeVerseNum === v.numberInSurah ? stopAllAudio() : playVerse(v)}
                  style={{ width: '32px', height: '32px', borderRadius: '16px', backgroundColor: activeVerseNum === v.numberInSurah ? colors.accent : colors.background, border: 'none', cursor: 'pointer', fontSize: '16px' }}
                >
                  {activeVerseNum === v.numberInSurah ? '⏸' : '▶'}
                </button>
              </div>
              <div style={{ fontSize: '24px', lineHeight: '40px', textAlign: 'right', color: colors.tint, marginBottom: '12px' }}>{v.text}</div>
              <div style={{ fontSize: '14px', color: colors.muted, fontStyle: 'italic', lineHeight: '20px' }}>{v.translation}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const DuasPage: React.FC = () => {
  const { colors } = useTheme();
  const duas = [
    { title: 'Morning', arabic: 'أَصْبَحْنَا وَأَصْبَحَ الْمُلْكُ لِلَّهِ', translation: 'We have reached the morning and at this very time unto Allah belongs all sovereignty' },
    { title: 'Evening', arabic: 'أَمْسَيْنَا وَأَمْسَى الْمُلْكُ لِلَّهِ', translation: 'We have reached the evening and at this very time unto Allah belongs all sovereignty' },
  ];
  
  return (
    <div style={{ padding: '20px', backgroundColor: colors.background, color: colors.text, minHeight: '100vh' }}>
      <h2>Daily Duas</h2>
      {duas.map((d, i) => (
        <div key={i} style={{ backgroundColor: colors.cardBackground, padding: '20px', borderRadius: '12px', marginTop: '16px', border: `1px solid ${colors.border}` }}>
          <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>{d.title}</div>
          <div style={{ fontSize: '20px', textAlign: 'right', marginBottom: '12px' }}>{d.arabic}</div>
          <div style={{ fontSize: '14px', opacity: 0.8 }}>{d.translation}</div>
        </div>
      ))}
    </div>
  );
};

export const StatsPage: React.FC = () => {
  const { colors } = useTheme();
  const { manualLocation, ishaFixedSettings, fajrFixedSettings, calcMethod, madhab } = useGlobalStorageContext();
  const [stats, setStats] = React.useState<Array<{title: string; value: string; desc: string; icon: string}> | null>(null);

  React.useEffect(() => {
    const calculateStats = async () => {
      try {
        const { Coordinates, CalculationMethod, PrayerTimes, Madhab } = await import('adhan');
        const coordinates = new Coordinates(manualLocation.latitude, manualLocation.longitude);
        const params = (CalculationMethod as any)[calcMethod]();
        
        params.madhab = madhab === 'Hanafi' ? Madhab.Hanafi : Madhab.Shafi;
        
        let prayerTimes: any = new PrayerTimes(coordinates, new Date(), params);
        
        if (ishaFixedSettings.mode === 'afterMaghrib') {
          const ishaTime = new Date(prayerTimes.maghrib.getTime() + ishaFixedSettings.minutesAfterMaghrib * 60000);
          prayerTimes = { ...prayerTimes, isha: ishaTime };
        } else if (ishaFixedSettings.mode === 'fixedTime') {
          const [h, m] = ishaFixedSettings.fixedTime.split(':').map(Number);
          const ishaTime = new Date(prayerTimes.isha);
          ishaTime.setHours(h, m, 0, 0);
          prayerTimes = { ...prayerTimes, isha: ishaTime };
        }
        
        if (fajrFixedSettings.mode === 'fixedTime') {
          const [h, m] = fajrFixedSettings.fixedTime.split(':').map(Number);
          const fajrTime = new Date(prayerTimes.fajr);
          fajrTime.setHours(h, m, 0, 0);
          prayerTimes = { ...prayerTimes, fajr: fajrTime };
        }

        const formatInterval = (ms: number) => {
          const totalMinutes = Math.floor(ms / (1000 * 60));
          const hours = Math.floor(totalMinutes / 60);
          const mins = totalMinutes % 60;
          return `${hours}h ${mins}m`;
        };

        setStats([
          {
            title: 'Fajr to Maghrib',
            desc: 'Daylight hours',
            value: formatInterval(prayerTimes.maghrib.getTime() - prayerTimes.fajr.getTime()),
            icon: '☀️'
          },
          {
            title: 'Dhuhr to Asr',
            desc: 'Afternoon period',
            value: formatInterval(prayerTimes.asr.getTime() - prayerTimes.dhuhr.getTime()),
            icon: '⏰'
          },
          {
            title: 'Sunrise to Sunset',
            desc: 'Full daylight',
            value: formatInterval(prayerTimes.maghrib.getTime() - prayerTimes.sunrise.getTime()),
            icon: '🌙'
          },
          {
            title: 'Fajr to Sunrise',
            desc: 'Dawn period',
            value: formatInterval(prayerTimes.sunrise.getTime() - prayerTimes.fajr.getTime()),
            icon: '⭐'
          },
          {
            title: 'Maghrib to Isha',
            desc: 'Evening period',
            value: formatInterval(prayerTimes.isha.getTime() - prayerTimes.maghrib.getTime()),
            icon: '◻️'
          }
        ]);
      } catch (error) {
        console.error('Error calculating stats:', error);
        setStats([]);
      }
    };

    calculateStats();
  }, [manualLocation, ishaFixedSettings, fajrFixedSettings, calcMethod, madhab]);
  
  return (
    <div style={{ padding: '20px', backgroundColor: colors.background, color: colors.text, minHeight: '100vh' }}>
      <h2 style={{ color: colors.tint, marginBottom: '8px' }}>Prayer Statistics</h2>
      <p style={{ fontSize: '12px', color: colors.muted, marginBottom: '16px' }}>Prayer time intervals</p>
      {stats ? (stats.length > 0 ? stats.map((s, i) => (
        <div key={i} style={{ backgroundColor: colors.cardBackground, padding: '20px', borderRadius: '24px', marginBottom: '16px', border: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '16px', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', fontSize: '24px' }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{s.title}</div>
              <div style={{ fontSize: '10px', color: colors.muted, textTransform: 'uppercase' }}>{s.desc}</div>
            </div>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: colors.tint }}>{s.value}</div>
        </div>
      )) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>⚠️</div>
          <div>Error loading statistics</div>
        </div>
      )) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
          <div>Loading statistics...</div>
        </div>
      )}
    </div>
  );
};

export const InheritancePage: React.FC = () => {
  const { colors } = useTheme();
  const [estateValue, setEstateValue] = React.useState<string>('');
  const [hasHusband, setHasHusband] = React.useState(false);
  const [hasWife, setHasWife] = React.useState(false);
  const [hasFather, setHasFather] = React.useState(false);
  const [hasMother, setHasMother] = React.useState(false);
  const [numSons, setNumSons] = React.useState(0);
  const [numDaughters, setNumDaughters] = React.useState(0);

  const inheritanceDistribution = React.useMemo(() => {
    const estate = parseFloat(estateValue) || 0;
    if (estate <= 0) return [];

    let remaining = 1.0;
    const results: { label: string; percentage: number; amount: number }[] = [];
    const hasChildren = numSons > 0 || numDaughters > 0;

    if (hasHusband && !hasWife) {
      const share = hasChildren ? 0.25 : 0.5;
      results.push({ label: 'Husband', percentage: share * 100, amount: estate * share });
      remaining -= share;
    } else if (hasWife && !hasHusband) {
      const share = hasChildren ? 0.125 : 0.25;
      results.push({ label: 'Wife', percentage: share * 100, amount: estate * share });
      remaining -= share;
    }

    if (hasMother) {
      const share = hasChildren ? 1/6 : 1/3;
      results.push({ label: 'Mother', percentage: share * 100, amount: estate * share });
      remaining -= share;
    }

    if (hasFather) {
      const share = 1/6;
      if (hasChildren) {
        results.push({ label: 'Father', percentage: share * 100, amount: estate * share });
        remaining -= share;
      }
    }

    if (hasChildren) {
      const sonShare = 2;
      const daughterShare = 1;
      const totalUnits = (numSons * sonShare) + (numDaughters * daughterShare);
      
      if (totalUnits > 0) {
        const unitValue = remaining / totalUnits;
        if (numSons > 0) {
          const share = unitValue * sonShare;
          results.push({ label: `Son (x${numSons})`, percentage: share * 100 * numSons, amount: estate * share * numSons });
        }
        if (numDaughters > 0) {
          const share = unitValue * daughterShare;
          results.push({ label: `Daughter (x${numDaughters})`, percentage: share * 100 * numDaughters, amount: estate * share * numDaughters });
        }
        remaining = 0;
      }
    } else if (hasFather) {
      const existingFather = results.find(r => r.label === 'Father');
      if (existingFather) {
        existingFather.percentage += remaining * 100;
        existingFather.amount += estate * remaining;
      } else {
        results.push({ label: 'Father', percentage: remaining * 100, amount: estate * remaining });
      }
      remaining = 0;
    }

    return results;
  }, [estateValue, hasHusband, hasWife, hasFather, numSons, numDaughters, hasMother]);

  return (
    <div style={{ padding: '20px', backgroundColor: colors.background, color: colors.text, minHeight: '100vh' }}>
      <h2 style={{ color: colors.tint, marginBottom: '8px' }}>Inheritance Calculator</h2>
      <p style={{ fontSize: '12px', color: colors.muted, marginBottom: '16px' }}>Calculate Islamic inheritance distribution</p>
      
      <div style={{ backgroundColor: colors.cardBackground, borderRadius: '24px', padding: '24px', border: `1px solid ${colors.border}` }}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: colors.muted, marginBottom: '8px', textTransform: 'uppercase' }}>Estate Value</label>
          <input
            type="number"
            value={estateValue}
            onChange={(e) => setEstateValue(e.target.value)}
            placeholder="0.00"
            style={{ width: '100%', backgroundColor: colors.background, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '12px 16px', fontSize: '16px', color: colors.text }}
          />
        </div>

        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: colors.muted, marginBottom: '8px', textTransform: 'uppercase' }}>Select Heirs</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
          {[
            { label: 'Husband', val: hasHusband, set: setHasHusband, disable: hasWife },
            { label: 'Wife', val: hasWife, set: setHasWife, disable: hasHusband },
            { label: 'Father', val: hasFather, set: setHasFather, disable: false },
            { label: 'Mother', val: hasMother, set: setHasMother, disable: false },
          ].map((heir, i) => (
            <button
              key={i}
              disabled={heir.disable}
              onClick={() => heir.set(!heir.val)}
              style={{ flex: '1 1 48%', padding: '8px 12px', borderRadius: '16px', backgroundColor: heir.val ? colors.tint : colors.background, border: `1px solid ${colors.border}`, color: heir.val ? '#fff' : colors.text, fontWeight: '600', cursor: heir.disable ? 'not-allowed' : 'pointer', opacity: heir.disable ? 0.3 : 1 }}
            >
              {heir.label}
            </button>
          ))}
        </div>
        
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: colors.background, padding: '16px', borderRadius: '16px', marginBottom: '8px', border: `1px solid ${colors.border}` }}>
            <span style={{ fontSize: '14px', fontWeight: '600' }}>Sons</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button onClick={() => setNumSons(Math.max(0, numSons - 1))} style={{ width: '32px', height: '32px', borderRadius: '16px', backgroundColor: colors.cardBackground, border: `1px solid ${colors.border}`, fontSize: '18px', fontWeight: 'bold', color: colors.tint, cursor: 'pointer' }}>-</button>
              <span style={{ fontSize: '16px', fontWeight: 'bold', minWidth: '20px', textAlign: 'center' }}>{numSons}</span>
              <button onClick={() => setNumSons(numSons + 1)} style={{ width: '32px', height: '32px', borderRadius: '16px', backgroundColor: colors.cardBackground, border: `1px solid ${colors.border}`, fontSize: '18px', fontWeight: 'bold', color: colors.tint, cursor: 'pointer' }}>+</button>
            </div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: colors.background, padding: '16px', borderRadius: '16px', marginBottom: '8px', border: `1px solid ${colors.border}` }}>
            <span style={{ fontSize: '14px', fontWeight: '600' }}>Daughters</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button onClick={() => setNumDaughters(Math.max(0, numDaughters - 1))} style={{ width: '32px', height: '32px', borderRadius: '16px', backgroundColor: colors.cardBackground, border: `1px solid ${colors.border}`, fontSize: '18px', fontWeight: 'bold', color: colors.tint, cursor: 'pointer' }}>-</button>
              <span style={{ fontSize: '16px', fontWeight: 'bold', minWidth: '20px', textAlign: 'center' }}>{numDaughters}</span>
              <button onClick={() => setNumDaughters(numDaughters + 1)} style={{ width: '32px', height: '32px', borderRadius: '16px', backgroundColor: colors.cardBackground, border: `1px solid ${colors.border}`, fontSize: '18px', fontWeight: 'bold', color: colors.tint, cursor: 'pointer' }}>+</button>
            </div>
          </div>
        </div>

        {inheritanceDistribution.length > 0 && (
          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.tint, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>DISTRIBUTION</div>
            {inheritanceDistribution.map((res, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', backgroundColor: colors.background, borderRadius: '16px', marginBottom: '8px', border: `1px solid ${colors.border}` }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{res.label}</div>
                  <div style={{ fontSize: '10px', color: colors.tint, fontWeight: 'bold', textTransform: 'uppercase' }}>
                    {res.percentage.toFixed(2)}% share
                  </div>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: colors.tint }}>
                  ${res.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            ))}
            <div style={{ fontSize: '10px', color: colors.muted, textAlign: 'center', marginTop: '12px', lineHeight: '16px' }}>
              This is a simplified calculation. Consult a scholar for complex cases.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const HalalPage: React.FC = () => {
  const { colors } = useTheme();
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [results, setResults] = React.useState<any[]>([]);
  const [coords, setCoords] = React.useState<{ lat: number; lng: number } | null>(null);
  const [debugInfo, setDebugInfo] = React.useState<string>('');
  const [initialized, setInitialized] = React.useState(false);

  const searchHalalPlaces = async (lat: number, lng: number, query: string) => {
    try {
      setDebugInfo(`Searching at: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        setDebugInfo('Google Maps API key not configured');
        return [];
      }

      const baseUrl = 'https://maps.googleapis.com/maps/api/place/nearbysearch/json';
      const params = new URLSearchParams({
        location: `${lat},${lng}`,
        radius: '10000',
        keyword: query || 'halal restaurant',
        key: apiKey
      });
      
      let data;
      
      try {
        const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(`${baseUrl}?${params}`)}`;
        const response = await fetch(proxyUrl);
        const proxyData = await response.json();
        data = JSON.parse(proxyData.contents);
        setDebugInfo(`API: ${data.status}`);
      } catch (error) {
        setDebugInfo(`Search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return [];
      }
      
      if (data.status === 'OK') {
        setDebugInfo(`Found ${data.results?.length || 0} results`);
        return data.results || [];
      } else if (data.status === 'ZERO_RESULTS') {
        setDebugInfo('No halal places found in area');
        return [];
      } else {
        setDebugInfo(`API Error: ${data.status} - ${data.error_message || 'Unknown error'}`);
        return [];
      }
    } catch (error) {
      setDebugInfo(`Search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return [];
    }
  };

  const handleSearch = async (lat?: number, lng?: number, searchQuery?: string) => {
    let activeLat = lat || coords?.lat;
    let activeLng = lng || coords?.lng;
    const query = searchQuery !== undefined ? searchQuery : search;
    
    if (query && query.trim() && !lat && !lng) {
      setLoading(true);
      try {
        const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
        const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(geocodeUrl)}`;
        const response = await fetch(proxyUrl);
        const proxyData = await response.json();
        const data = JSON.parse(proxyData.contents);
        
        if (data.results && data.results.length > 0) {
          activeLat = data.results[0].geometry.location.lat;
          activeLng = data.results[0].geometry.location.lng;
          if (activeLat && activeLng) {
            setDebugInfo(`Geocoded "${query}" to: ${activeLat.toFixed(4)}, ${activeLng.toFixed(4)}`);
          }
        }
      } catch (error) {
        setDebugInfo(`Geocoding failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    
    if (!activeLat || !activeLng) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const places = await searchHalalPlaces(activeLat, activeLng, query || "halal restaurant");
      setResults(places);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (initialized) return;
    
    const initLocation = async () => {
      const cached = localStorage.getItem('halal_location');
      if (cached) {
        const { latitude, longitude } = JSON.parse(cached);
        setCoords({ lat: latitude, lng: longitude });
        await handleSearch(latitude, longitude, '');
        setInitialized(true);
        return;
      }

      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setCoords({ lat: latitude, lng: longitude });
            localStorage.setItem('halal_location', JSON.stringify({ latitude, longitude }));
            await handleSearch(latitude, longitude, '');
            setInitialized(true);
          },
          () => {
            setLoading(false);
            setInitialized(true);
          }
        );
      } else {
        setLoading(false);
        setInitialized(true);
      }
    };
    initLocation();
  }, [initialized]);

  const handleLocationPress = (placeId: string) => {
    window.open(`https://www.google.com/maps/place/?q=place_id:${placeId}`, '_blank');
  };

  return (
    <div style={{ backgroundColor: colors.background, minHeight: '100vh', color: colors.text }}>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', backgroundColor: colors.cardBackground, borderRadius: '16px', border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
          <input
            type="text"
            placeholder="Search halal places"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            style={{ flex: 1, padding: '12px 16px', fontSize: '16px', border: 'none', backgroundColor: 'transparent', color: colors.text }}
          />
          <button
            onClick={() => handleSearch()}
            disabled={loading}
            style={{ backgroundColor: colors.tint, padding: '0 16px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}
          >
            <span style={{ color: '#fff', fontSize: '20px' }}>🔍</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '64px 16px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
          <div style={{ color: colors.tint, fontSize: '16px' }}>Searching...</div>
        </div>
      ) : (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {results.map((place, i) => (
            <div
              key={i}
              onClick={() => handleLocationPress(place.place_id)}
              style={{ backgroundColor: colors.cardBackground, borderRadius: '16px', padding: '16px', border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div style={{ backgroundColor: colors.background, padding: '12px', borderRadius: '12px', marginRight: '12px' }}>
                  <span style={{ fontSize: '20px' }}>🍽️</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>{place.name}</div>
                  <div style={{ fontSize: '14px', color: colors.muted, marginBottom: '2px' }}>{place.vicinity}</div>
                  <div style={{ fontSize: '12px', color: colors.tint }}>⭐ {place.rating || 'N/A'} • View on Maps</div>
                </div>
              </div>
              <span style={{ color: colors.muted, fontSize: '20px' }}>›</span>
            </div>
          ))}
          
          {results.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '32px' }}>
              <div style={{ color: colors.muted, fontSize: '16px', marginBottom: '8px' }}>No results found</div>
              {debugInfo && (
                <div style={{ fontSize: '12px', color: colors.muted, fontFamily: 'monospace' }}>Debug: {debugInfo}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const TrainsPage: React.FC = () => {
  const { colors } = useTheme();
  const [trains, setTrains] = React.useState<Array<{time: string; track: string; train: string; destination: string; note: string; id: string}>>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('Helsinki Asema');
  const [areaCode, setAreaCode] = React.useState('HKI');
  const [areaName, setAreaName] = React.useState('Helsinki Asema');
  const [showList, setShowList] = React.useState(false);
  const [filterItems, setFilterItems] = React.useState<Array<{stationName: string; stationShortCode: string; latitude?: number; longitude?: number}>>([]);
  const [stations, setStations] = React.useState<Array<{stationName: string; stationShortCode: string; latitude?: number; longitude?: number}>>([]);
  const [loadingLocation, setLoadingLocation] = React.useState(false);
  const [showFavorites, setShowFavorites] = React.useState(false);
  const [favorites, setFavorites] = React.useState<Array<{stationName: string; stationShortCode: string}>>([]);

  React.useEffect(() => {
    loadStations();
    restore();
    loadFavorites();
  }, []);

  const loadStations = async () => {
    try {
      const response = await fetch('https://rata.digitraffic.fi/api/v1/metadata/stations');
      const data = await response.json();
      const passengerStations = data.filter((s: any) => s.passengerTraffic).map((s: any) => ({
        stationName: s.stationName,
        stationShortCode: s.stationShortCode,
        latitude: s.latitude,
        longitude: s.longitude
      }));
      setStations(passengerStations);
    } catch (error) {
      console.error('Failed to load stations:', error);
    }
  };

  const restore = () => {
    try {
      const stored = localStorage.getItem('train_station');
      if (stored) {
        const data = JSON.parse(stored);
        setAreaCode(data.areaCode || 'HKI');
        setAreaName(data.areaName || 'Helsinki Asema');
        setSearchTerm(data.areaName || 'Helsinki Asema');
        fetchTrains(data.areaCode || 'HKI');
      } else {
        fetchTrains('HKI');
      }
    } catch (error) {
      fetchTrains('HKI');
    }
  };

  const saveSettings = () => {
    try {
      localStorage.setItem('train_station', JSON.stringify({ areaCode, areaName }));
      addToFavorites({ stationName: areaName, stationShortCode: areaCode });
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const loadFavorites = () => {
    try {
      const stored = localStorage.getItem('train_favorites');
      if (stored) {
        setFavorites(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  const addToFavorites = (station: {stationName: string; stationShortCode: string}) => {
    try {
      const stored = localStorage.getItem('train_favorites');
      let favs: Array<{stationName: string; stationShortCode: string}> = stored ? JSON.parse(stored) : [];
      favs = favs.filter(f => f.stationShortCode !== station.stationShortCode);
      favs.unshift(station);
      favs = favs.slice(0, 5);
      localStorage.setItem('train_favorites', JSON.stringify(favs));
      setFavorites(favs);
    } catch (error) {
      console.error('Failed to save favorites:', error);
    }
  };

  const fetchTrains = async (code: string) => {
    setLoading(true);
    try {
      const url = `https://rata.digitraffic.fi/api/v1/live-trains?station=${code}&arrived_trains=1&arriving_trains=100&departing_trains=100&departing_trains=5`;
      const response = await fetch(url);
      const data = await response.json();
      handleTrainData(data, code);
    } catch (error) {
      console.error('Failed to fetch trains:', error);
      setTrains([]);
    } finally {
      setLoading(false);
    }
  };

  const getStationName = (code: string) => {
    const station = stations.find(s => s.stationShortCode === code);
    return station ? station.stationName : code;
  };

  const getStationData = (times: any[], stationCode: string) => {
    let data = null;
    let index = 0;
    
    for (let i = 0; i < times.length; i++) {
      if (times[i].stationShortCode === stationCode && times[i].type === 'DEPARTURE') {
        data = times[i];
        index = i;
        break;
      }
    }

    let destination = '';
    if (data) {
      for (let i = index; i < times.length; i++) {
        if ((times[i].stationShortCode === 'LEN' || times[i].stationShortCode === 'HKI') && times[i].type === 'ARRIVAL') {
          destination = getStationName(times[i].stationShortCode);
          break;
        }
      }
      if (!destination) {
        destination = getStationName(times[times.length - 1].stationShortCode);
      }
    }

    return { data, destination };
  };

  const handleTrainData = (data: any[], stationCode: string) => {
    const tmpRows: Array<{time: string; track: string; train: string; destination: string; note: string; id: string}> = [];
    if (data && data.length) {
      for (let i = 0; i < data.length; i++) {
        const ret = getStationData(data[i].timeTableRows, stationCode);
        if (!ret.data) continue;

        const date = new Date(ret.data.scheduledTime);
        const differenceInMinutes = Number(ret.data.differenceInMinutes || 0);
        
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const time = `${hours}:${minutes}`;

        let note = ret.data.cancelled ? 'Cancelled' : '';
        if (!note && differenceInMinutes > 0) {
          const noteDate = new Date(date.getTime() + differenceInMinutes * 60 * 1000);
          const noteHours = String(noteDate.getHours()).padStart(2, '0');
          const noteMinutes = String(noteDate.getMinutes()).padStart(2, '0');
          note = `=>${noteHours}:${noteMinutes}`;
        }

        const trainId = data[i].commuterLineID || data[i].trainType + data[i].trainNumber;
        tmpRows.push({
          time,
          track: ret.data.commercialTrack || '-',
          train: trainId,
          destination: ret.destination,
          note: note || '',
          id: `${date.getTime()}-${trainId}`
        });
      }
    }
    tmpRows.sort((a, b) => parseInt(a.id.split('-')[0]) - parseInt(b.id.split('-')[0]));
    setTrains(tmpRows.slice(0, 20));
  };

  const filterStations = (text: string) => {
    setSearchTerm(text);
    if (text && text.trim()) {
      const filtered = stations.filter(s => 
        s.stationName.toLowerCase().includes(text.toLowerCase())
      );
      setFilterItems(filtered);
      setShowList(true);
    } else {
      setFilterItems([]);
      setShowList(false);
    }
  };

  const selectStation = (station: {stationName: string; stationShortCode: string}) => {
    setSearchTerm(station.stationName);
    setAreaName(station.stationName);
    setAreaCode(station.stationShortCode);
    setShowList(false);
    fetchTrains(station.stationShortCode);
  };

  const useCurrentLocation = async () => {
    setLoadingLocation(true);
    try {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const nearest = findNearestStation(latitude, longitude);
            if (nearest) {
              selectStation(nearest);
            }
            setLoadingLocation(false);
          },
          () => {
            alert('Failed to get current location');
            setLoadingLocation(false);
          }
        );
      }
    } catch (error) {
      alert('Error getting location');
      setLoadingLocation(false);
    }
  };

  const findNearestStation = (lat: number, lng: number) => {
    if (stations.length === 0) return null;
    
    let nearest = stations[0];
    let minDistance = calculateDistance(lat, lng, nearest.latitude || 0, nearest.longitude || 0);
    
    for (const station of stations) {
      if (!station.latitude || !station.longitude) continue;
      const distance = calculateDistance(lat, lng, station.latitude, station.longitude);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = station;
      }
    }
    
    return nearest;
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  React.useEffect(() => {
    if (areaCode && areaName) {
      saveSettings();
    }
  }, [areaCode, areaName]);

  return (
    <div style={{ backgroundColor: colors.background, minHeight: '100vh', color: colors.text }}>
      <div style={{ padding: '16px', backgroundColor: colors.cardBackground, display: 'flex', gap: '12px', alignItems: 'center', borderBottom: `1px solid ${colors.border}` }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => filterStations(e.target.value)}
          placeholder="Select Station"
          style={{ flex: 1, backgroundColor: colors.background, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '12px 16px', fontSize: '16px', color: colors.text }}
        />
        <button onClick={() => setShowFavorites(!showFavorites)} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px' }} title="Favorites">⭐</button>
        <button onClick={useCurrentLocation} disabled={loadingLocation} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px' }} title="Use Current Location">
          {loadingLocation ? '⏳' : '📍'}
        </button>
        <button onClick={() => fetchTrains(areaCode)} style={{ padding: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px' }} title="Refresh">🔄</button>
      </div>

      {showFavorites && favorites.length > 0 && (
        <div style={{ backgroundColor: colors.cardBackground, margin: '16px', borderRadius: '12px', padding: '8px', border: `1px solid ${colors.border}` }}>
          {favorites.map((fav, index) => (
            <div
              key={index}
              onClick={() => { selectStation(fav); setShowFavorites(false); }}
              style={{ padding: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', borderRadius: '8px' }}
            >
              <span>⭐</span>
              <span>{fav.stationName}</span>
            </div>
          ))}
        </div>
      )}

      {showList && filterItems.length > 0 && (
        <div style={{ maxHeight: '200px', overflow: 'auto', backgroundColor: colors.cardBackground, borderBottom: `1px solid ${colors.border}` }}>
          {filterItems.map((station, index) => (
            <div
              key={index}
              onClick={() => selectStation(station)}
              style={{ padding: '16px', cursor: 'pointer', borderBottom: `1px solid ${colors.border}` }}
            >
              {station.stationName}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', backgroundColor: colors.tint, padding: '12px 8px', color: '#fff', fontWeight: 'bold', fontSize: '12px' }}>
        <div style={{ width: '18%' }}>Time</div>
        <div style={{ width: '12%' }}>Track</div>
        <div style={{ width: '15%' }}>Train</div>
        <div style={{ width: '40%' }}>Destination</div>
        <div style={{ width: '15%' }}>Note</div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '32px' }}>⏳</div>
          <div>Loading...</div>
        </div>
      ) : trains.length > 0 ? (
        <div>
          {trains.map((train) => (
            <div key={train.id} style={{ display: 'flex', padding: '12px 8px', borderBottom: `1px solid ${colors.border}`, fontSize: '12px' }}>
              <div style={{ width: '18%' }}>{train.time}</div>
              <div style={{ width: '12%' }}>{train.track}</div>
              <div style={{ width: '15%' }}>{train.train}</div>
              <div style={{ width: '40%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{train.destination}</div>
              <div style={{ width: '15%' }}>{train.note}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.muted }}>No trains found</div>
      )}

      <div style={{ padding: '12px', backgroundColor: colors.cardBackground, borderTop: `1px solid ${colors.border}`, fontSize: '10px', color: colors.muted, textAlign: 'center' }}>
        Data from Liikennevirasto Digital API. Licensed under Creative Commons Attribution 4.0
      </div>
    </div>
  );
};

export const AppInfoPage: React.FC = () => {
  const { colors } = useTheme();
  return (
    <div style={{ padding: '20px', backgroundColor: colors.background, color: colors.text, minHeight: '100vh' }}>
      <h2>App Info</h2>
      <div style={{ backgroundColor: colors.cardBackground, padding: '24px', borderRadius: '16px', marginTop: '20px', border: `1px solid ${colors.border}` }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>Rukousajat</div>
        <div style={{ marginBottom: '8px' }}>Version: 1.0.0</div>
        <div style={{ fontSize: '14px', opacity: 0.7, marginTop: '16px' }}>Prayer times and Islamic utilities for Finland</div>
      </div>
    </div>
  );
};
